import {
  Group,
  Modal,
  NumberInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { showNotification } from "@mantine/notifications"
import { IconX } from "@tabler/icons-react"
import type { Document } from "@tfg/db"
import {
  Button,
  ActionIcon,
  ErrorBox,
  DateInput,
  DocumentUploader,
  DocumentActionsCell,
} from "@tfg/ui"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import z from "zod"

import { queryClient } from "~/backend/queryClient"
import {
  findInvoicesByOrganizationIdQueryKey,
  useCreateInvoice,
} from "~/gen/api"
import { fetchDocumentUrl } from "~/hooks/useDocumentsQuery"
import { useUploadDocumentsMutation } from "~/utils/documents"

type Props = {
  opened: boolean
  onClose: () => void
}

const invoiceSchema = z.object({
  reference: z.string(),
  amount: z.number().min(0),
  issueDate: z.date(),
})

export type InvoiceForm = z.infer<typeof invoiceSchema>

export const InvoiceCreationModal = ({ opened, onClose }: Props) => {
  const params = useParams()
  const organizationId = params.organizationId?.toString() ?? ""
  const { t } = useTranslation(["admin", "common"])

  const [uploadedDocument, setUploadedDocument] = useState<
    Document | undefined
  >(undefined)

  const form = useForm<InvoiceForm>({
    validate: zod4Resolver(invoiceSchema),
  })

  const {
    mutateAsync: createInvoice,
    isPending: isLoading,
    error,
  } = useCreateInvoice()

  const { mutateAsync: uploadDocuments, isPending: isUploading } =
    useUploadDocumentsMutation({
      onSuccess: (documents) => setUploadedDocument(documents[0]),
    })

  return (
    <Modal opened={opened} onClose={onClose} title={t("invoices.modal.title")}>
      <form
        onSubmit={form.onSubmit(async (values) => {
          if (!uploadedDocument) return
          await createInvoice(
            {
              data: {
                ...values,
                documentId: uploadedDocument.id,
                organizationId,
              },
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: findInvoicesByOrganizationIdQueryKey(),
                })
                onClose()
                form.reset()
                showNotification({
                  color: "green",
                  message: t("invoices.modal.success"),
                })
              },
            }
          )
        })}
      >
        <Stack>
          <ErrorBox error={error} />
          <Stack gap="sm">
            <DocumentUploader
              onDrop={uploadDocuments}
              isLoading={isUploading}
              title={t("common:documentUpload.title")}
              helpText={t("common:documentUpload.helptext")}
            />
            {uploadedDocument && (
              <Group wrap="nowrap">
                <Text fz="sm" lineClamp={1}>
                  {uploadedDocument.originalName}
                </Text>
                <DocumentActionsCell
                  documentName={uploadedDocument.originalName}
                  documentId={uploadedDocument.id}
                  fetchDownloadUrl={() => {
                    return fetchDocumentUrl(uploadedDocument.id, true)
                  }}
                  fetchViewUrl={() => fetchDocumentUrl(uploadedDocument.id)}
                />
                <ActionIcon
                  onClick={() => setUploadedDocument(undefined)}
                  icon={<IconX />}
                />
              </Group>
            )}
          </Stack>
          <TextInput
            label={t("invoices.modal.labels.reference")}
            placeholder={t("invoices.modal.placeholders.reference")}
            {...form.getInputProps("reference")}
          />
          <DateInput
            label={t("invoices.modal.labels.issueDate")}
            placeholder={t("invoices.modal.placeholders.issueDate")}
            {...form.getInputProps("issueDate")}
            style={{
              zIndex: 1000,
            }}
          />
          <NumberInput
            label={t("invoices.modal.labels.amount")}
            placeholder={t("invoices.modal.placeholders.amount")}
            decimalSeparator=","
            prefix="€ "
            thousandSeparator="."
            decimalScale={2}
            step={0.01}
            hideControls
            min={0}
            {...form.getInputProps("amount")}
          />
          <Button
            type="submit"
            disabled={!form.isValid() || !uploadedDocument}
            loading={isLoading}
          >
            {t("invoices.modal.submit")}
          </Button>
        </Stack>
      </form>
    </Modal>
  )
}
